import { FormattedMessage, useIntl } from 'react-intl'
import Step from 'components/global/Step'
import AccommodationInfo from './sections/AccommodationInfo'
import AccommodationModal from './AccommodationModal'
import { useEffect, useState } from 'react'
import PlanYourStay from './sections/PlanYourStay'
import AssignGuests from './sections/AssignGuests'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import BungalowOffer from './sections/BungalowOffer'
import Box from 'components/shared/Box'
import Button from 'components/shared/Button'
import { useApp } from 'store/steps'
import { STEPS, CREATOR_ACCOMMODATION_STEPS } from 'routes/steps'
import { useCart } from 'store/cart-slice/hooks'
import {
    removeAccommodationFromStore,
    setAccommodationInfoModalVisible,
    setAssignGuestsVisible,
    setBookingPersonDetails,
    setBungalows,
    setBungalowsVisible,
    setDates,
    setSpecialRequests,
    updateGuestsToAssign,
} from 'store/accommodation-slice'
import { createBungalows } from './helpers'
import { pushDataLayer } from 'helpers/analytics'
import AccommodationInfoAside from 'components/global/AccommodationInfoAside'
import { useResolutions } from 'store/resolutions'
import OfferError from './sections/OfferError'
import { scrollTo } from 'helpers/animateScroll'

const Accommodation = () => {
    const dispatch = useAppDispatch()
    const { isTablet } = useResolutions()
    const { formatMessage } = useIntl()
    const { stepIndex, navigateToStep, isCreatorAccommodation } = useApp()
    const {
        isCartEmpty,
        isCartFetching,
        isStepEmpty,
        removeNextStepsFromCart,
    } = useCart()

    const {
        isAssignGuestsVisible,
        areBungalowsVisible,
        bungalows,
        isAccommodationInfoModalVisible,
    } = useAppSelector((state) => state.accommodation)
    const cartAccommodation = useAppSelector(
        (state) => state.cart.items.accommodation
    )

    const [isSkipButtonDisabled, setIsSkipButtonDisabled] = useState(true)
    const [areBungalowsLoading, setAreBungalowsLoading] = useState(false)
    const [hasBungalowsOfferError, setHasBungalowsOfferError] = useState(false)

    const steps = isCreatorAccommodation ? CREATOR_ACCOMMODATION_STEPS : STEPS

    const areStepsFulfilled =
        isAssignGuestsVisible &&
        areBungalowsVisible &&
        bungalows.every((bungalow) => bungalow.room)

    const canSkipAccomodationStep =
        (!isSkipButtonDisabled && !areStepsFulfilled) ||
        (hasBungalowsOfferError && isCreatorAccommodation)

    const handleSwitchAccommodationModal = () => {
        dispatch(
            setAccommodationInfoModalVisible(!isAccommodationInfoModalVisible)
        )
    }

    const skipToNextStep = () => {
        // < analytics
        pushDataLayer({
            event: 'funnelStep',
            event_category: !isCreatorAccommodation
                ? 'tickets'
                : 'accommodation',
            event_action: 'accommodation_params_selected',
            event_label: '',
        })
        // analytics >
        navigateToStep({ stepId: steps[stepIndex + 2].id })
    }

    const handleOnFetched = (isSuccess: boolean) => {
        setAreBungalowsLoading(false)
        setHasBungalowsOfferError(!isSuccess)
    }

    useEffect(() => {
        setHasBungalowsOfferError(false)
        setAreBungalowsLoading(
            areBungalowsVisible && isStepEmpty('accommodation')
        )
    }, [areBungalowsVisible])

    useEffect(() => {
        if (!hasBungalowsOfferError) return

        scrollTo({
            id: 'accommodation-bungalows-error',
            duration: 500,
        })
    }, [hasBungalowsOfferError])

    useEffect(() => {
        const shouldSkipBeDisabled =
            !areStepsFulfilled && (isCartEmpty || isCartFetching)

        setIsSkipButtonDisabled(shouldSkipBeDisabled)
    }, [isCartEmpty, isCartFetching])

    useEffect(() => {
        // < analytics
        pushDataLayer({
            event: 'productPresented',
            ecommerce: {
                items: [
                    {
                        item_name: 'Accomodation',
                        item_brand: 'PoP',
                    },
                ],
            },
        })
        // analytics >

        removeNextStepsFromCart({ activeStepId: 'accommodation' })

        if (!cartAccommodation) {
            dispatch(removeAccommodationFromStore())
            return
        }
        const { detail, productId } = cartAccommodation
        const {
            customerEmail,
            customerFirstName,
            customerLastName,
            customerPhone,
            specialRequests,
            rooms,
        } = detail

        if (!specialRequests) return
        const { guests, request } = specialRequests
        if (!guests) return

        const adults = guests.filter(
            ({ personType }) => personType === 'adult'
        ).length
        const kids = guests.filter(
            ({ personType }) => personType === 'kid'
        ).length
        const infants = guests.filter(
            ({ personType }) => personType === 'infant'
        ).length
        const toAssign = {
            total: adults + kids + infants,
            adults,
            kids,
            infants,
        }

        dispatch(setDates([detail.dateFrom, detail.dateTo]))
        dispatch(updateGuestsToAssign(toAssign))
        dispatch(setAssignGuestsVisible(true))
        dispatch(setBungalowsVisible(true))

        const bungalowsBoxId = Array.from(
            new Set(guests.map((guest) => guest.boxId || ''))
        )

        if (!productId || !rooms) return
        const bungalowsObject = createBungalows({
            guests,
            bungalowsBoxId,
            productId,
            rooms,
        })

        dispatch(setBungalows(bungalowsObject))
        dispatch(
            setBookingPersonDetails({
                field: 'name',
                value: customerFirstName,
            })
        )
        dispatch(
            setBookingPersonDetails({
                field: 'surname',
                value: customerLastName,
            })
        )
        dispatch(
            setBookingPersonDetails({
                field: 'email',
                value: customerEmail,
            })
        )
        dispatch(
            setBookingPersonDetails({
                field: 'phone',
                value: customerPhone,
            })
        )

        request && dispatch(setSpecialRequests(request))
    }, [])

    return (
        <Step
            title={formatMessage({
                id: 'global.steps.caption_accommo',
                defaultMessage: 'Nocleg',
            })}
            isButtonVisible={areStepsFulfilled}
        >
            {isCreatorAccommodation
                ? isTablet && !areBungalowsVisible && <AccommodationInfoAside />
                : (!isTablet || !areBungalowsVisible) && (
                      <AccommodationInfo
                          openModal={handleSwitchAccommodationModal}
                      />
                  )}

            <PlanYourStay />

            {isAssignGuestsVisible && (
                <AssignGuests
                    modalOpener={handleSwitchAccommodationModal}
                    isLoading={areBungalowsLoading}
                />
            )}

            {areBungalowsVisible &&
                !hasBungalowsOfferError &&
                bungalows.map((bungalow, index) => {
                    const { adults, kids, infants } = bungalow

                    return (
                        <BungalowOffer
                            key={`bungalow-${index}`}
                            index={index + 1}
                            modalOpener={handleSwitchAccommodationModal}
                            adults={adults.length}
                            kids={kids.length}
                            infants={infants.length}
                            onFetched={handleOnFetched}
                        />
                    )
                })}

            {hasBungalowsOfferError && (
                <OfferError
                    hasCta={canSkipAccomodationStep}
                    onCtaClick={skipToNextStep}
                />
            )}

            <AccommodationModal
                isOpened={isAccommodationInfoModalVisible}
                closeModal={handleSwitchAccommodationModal}
            />
            {canSkipAccomodationStep && (
                <Box direction="row" justify="flex-end" align="center" mt={24}>
                    <Button
                        variant="borderPrimary"
                        onClick={skipToNextStep}
                        testId="step-button-continue-without-accommo"
                    >
                        <FormattedMessage
                            id="global.btn.skip_accommodation"
                            defaultMessage="Kontynuuj bez noclegu"
                        />
                    </Button>
                </Box>
            )}
        </Step>
    )
}

export default Accommodation
